import React from 'react';
import { MyClientsOrderDeliveryMethod } from '@1po/1po-bff-fe-spec/generated/my_clients_order/model/MyClientsOrderDeliveryMethod';
import {
  MyClientsOrder,
  MyClientsOrderStatus,
} from '@1po/1po-bff-fe-spec/generated/my_clients_order/response/MyClientsOrderResponse';
import type { ColumnsType } from 'antd-v5/es/table';
import { TFunction } from 'i18next';
import { ROUTER_CLIENT_ORDER_DETAIL } from 'app/AppRouter';
import { CheckCircleIcon, CircleXMark, EnvelopeOutlinedIcon } from 'assets/icons';
import {
  MyClientsOrderDeliveryMethod as DeliveryMethod,
  MyClientsOrderFilterAndSortFieldLocal,
  MyClientsOrdersFilterAndSortField,
  MyClientsOrderStatus as OrderStatus,
} from 'domains/order/Order.types';
import { theme } from 'styles';
import { CenterFlex, Icon, IconButton, Link, StatusBubble, Text } from 'UI';
import { textFormatter } from 'utils';
import { isValidDate } from 'utils/format/formattingService';

export const MIN_SEARCH_LENGTH = 3;

export const getMyClientsOrderDeliveryMethod = (
  deliveryMethod: MyClientsOrderDeliveryMethod | undefined,
  t: TFunction,
): string => {
  switch (deliveryMethod) {
    case DeliveryMethod.DESK:
      return t('clients_order.delivery_method.desk', 'Desk');
    case DeliveryMethod.DIRECT:
      return t('clients_order.delivery_method.direct', 'Direct');
    case DeliveryMethod.EXADIS_PICKUP:
      return t('clients_order.delivery_method.exadis_pickup', 'Exadis pickup');
    case DeliveryMethod.UNKNOWN:
    default:
      return t('clients_order.delivery_method.unknown', 'Unknown');
  }
};

export const getMyClientsOrderStatus = (status: string, t: TFunction) => {
  const getStatusLabel = () => {
    switch (status) {
      case OrderStatus.CANCELLED:
        return t('clients_order.status.cancelled', 'Cancelled');
      case OrderStatus.DELIVERED:
        return t('clients_order.status.delivered', 'Delivered');
      case OrderStatus.IN_PROGRESS:
        return t('clients_order.status.in_progress', 'In progress');
      case OrderStatus.PROCESSING:
        return t('clients_order.status.processing', 'Processing');
      case OrderStatus.REJECTED:
        return t('clients_order.status.rejected', 'Rejected');
      case OrderStatus.TECHNICAL_ERROR:
        return t('clients_order.status.technical_error', 'Technical error');
      case OrderStatus.TO_BE_VALIDATED:
        return t('clients_order.status.to_be_validated', 'To be validated');
      case OrderStatus.UNKNOWN:
        return t('clients_order.status.unknown', 'Unknown');
      default:
        return status;
    }
  };

  const getStatusType = () => {
    switch (status) {
      case OrderStatus.DELIVERED:
      case OrderStatus.CANCELLED:
        return 'success';
      case OrderStatus.TECHNICAL_ERROR:
        return 'disabled';
      case OrderStatus.REJECTED:
      case OrderStatus.UNKNOWN:
        return 'error';
      case OrderStatus.IN_PROGRESS:
        return 'info';
      case OrderStatus.TO_BE_VALIDATED:
        return 'neutral';
      case OrderStatus.PROCESSING:
      default:
        return 'warning';
    }
  };

  return status.length > 0 && <StatusBubble statusType={getStatusType()}>{getStatusLabel()}</StatusBubble>;
};

export const getSupplierNameLabel = (supplierName: string, t: TFunction) => {
  switch (supplierName) {
    case 'RENAULT':
      return t('clients_order.supplier.renault', 'Renault');
    case 'EXADIS':
      return t('clients_order.supplier.exadis', 'Exadis');
    case 'SAP2B':
      return t('clients_order.supplier.sap2b', 'SAP to Business');
    default:
      return supplierName;
  }
};

export const getStatusBarLabels = (t: TFunction) => [
  {
    key: OrderStatus.TO_BE_VALIDATED,
    label: t('clients_order.status_tab.to_be_validated', 'Pending Validation'),
    'data-cy': 'mco-tab-to-be-reissued-in-dms',
  },
  {
    key: OrderStatus.TECHNICAL_ERROR,
    label: t('clients_order.status_tab.technical_error', 'To be reissued in DMS'),
    'data-cy': 'mco-tab-to-be-reissued-in-dms',
  },
  {
    key: OrderStatus.IN_PROGRESS,
    label: t('clients_order.status_tab.in_progress', 'On Going'),
    'data-cy': 'mco-tab-on-going',
  },
  {
    key: `${OrderStatus.DELIVERED}-${OrderStatus.CANCELLED}`,
    label: t('clients_order.status_tab.finished', 'Finished'),
    'data-cy': 'mco-tab-finihsed',
  },
  {
    key: '',
    label: t('clients_order.status_tab.all_orders', 'All orders'),
    'data-cy': 'mco-tab-all-orders',
  },
];

export const getStatusLabels = (t: TFunction) => [
  {
    title: t('clients_order.status.to_be_validated', 'To be validated'),
    value: OrderStatus.TO_BE_VALIDATED,
  },
  {
    title: t('clients_order.status.processing', 'Processing'),
    value: OrderStatus.PROCESSING,
  },
  {
    title: t('clients_order.status.in_progress', 'In progress'),
    value: OrderStatus.IN_PROGRESS,
  },
  {
    title: t('clients_order.status.delivered', 'Delivered'),
    value: OrderStatus.DELIVERED,
  },
  {
    title: t('clients_order.status.cancelled', 'Cancelled'),
    value: OrderStatus.CANCELLED,
  },
  {
    title: t('clients_order.status.rejected', 'Rejected'),
    value: OrderStatus.REJECTED,
  },
  {
    title: t('clients_order.status.technical_error', 'Technical error'),
    value: OrderStatus.TECHNICAL_ERROR,
  },
];

export interface MyClientsOrderStatusSelection {
  title: string;
  value: MyClientsOrderStatus;
  selected: boolean;
}

export const getEnhancedStatusLabels = (
  t: TFunction,
  filterMap?: Map<MyClientsOrderFilterAndSortFieldLocal, string>,
): MyClientsOrderStatusSelection[] => {
  const filteredStatus = (filterMap?.get(MyClientsOrdersFilterAndSortField.STATUS) ?? '').split('-');
  return getStatusLabels(t).map((e) => ({ ...e, selected: filteredStatus.includes(e.value) }));
};

export const getBooleanFilterOptions = (t: TFunction): { title: string; value: string }[] => [
  {
    title: t('common.yes', 'Yes'),
    value: 'true',
  },
  {
    title: t('common.no', 'No'),
    value: 'false',
  },
];

export const getSupplierFilterOptions = (t: TFunction): { title: string; value: string }[] => [
  {
    title: t('clients_order.supplier.renault', 'Renault'),
    value: 'RENAULT',
  },
  {
    title: t('clients_order.supplier.exadis', 'Exadis'),
    value: 'EXADIS',
  },
  {
    title: t('clients_order.supplier.sap2b', 'SAP to Business'),
    value: 'SAP2B',
  },
];

export const getStatusFilterPlaceholders = (t: TFunction, selected: MyClientsOrderStatusSelection[]) => {
  switch (selected.length) {
    case 0:
      return (
        <Text type="base" color="grey75" noWrap ellipsis>
          {t('order.order_detail.status', 'Status')}
        </Text>
      );
    case 1:
      return (
        <Text type={'text'} noWrap ellipsis>
          {selected[0].title}
        </Text>
      );
    default:
      return (
        <Text type={'text'} noWrap ellipsis>
          {t('order.order_detail.status.multi_selection', 'Multi selection')}
        </Text>
      );
  }
};

export const getMyClientsOrdersTableColumns = (t: TFunction): ColumnsType<MyClientsOrder> => [
  {
    title: <span data-cy={'col-header-order-number'}>{t('clients_order.order_number', 'Order ref.')}</span>,
    dataIndex: 'orderNumber',
    key: 'orderNumber',
    render: (orderNumber: string, record: MyClientsOrder) => (
      <CenterFlex data-cy={'col-order-number'}>
        <Link to={`${ROUTER_CLIENT_ORDER_DETAIL}/${record.uniqueId}`}>
          <Text
            type="sm"
            displayStyle={'link'}
            cursor={'pointer'}
            decoration={'underline'}
            data-cy={'order-number-link'}
          >
            {orderNumber ?? ''}
          </Text>
        </Link>
      </CenterFlex>
    ),
  },
  {
    title: (
      <span data-cy={'col-header-rpartstore-order-number'}>
        {t('clients_order.rpartstore_order_number', 'RPartStore Order ref.')}
      </span>
    ),
    dataIndex: 'rPartStoreOrderNumber',
    key: 'rPartStoreOrderNumber',
    render: (rPartStoreOrderNumber?: string) => (
      <CenterFlex data-cy={'col-result-rpartstore-order-number'}>{rPartStoreOrderNumber ?? ''}</CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-date'}>{t('common.date', 'Date')}</span>,
    dataIndex: 'orderDate',
    key: 'orderDate',
    render: (orderDate?: string) => {
      if (!orderDate || !isValidDate(orderDate)) {
        return null;
      }
      return (
        <CenterFlex data-cy={'col-result-date'}>{textFormatter.formatDateNumeric(new Date(orderDate))}</CenterFlex>
      );
    },
  },
  {
    title: <span data-cy={'col-header-distributed-by'}>{t('clients_order.supplier', 'Distributed by')}</span>,
    dataIndex: 'supplierName',
    key: 'supplierName',
    render: (supplierName?: string) => (
      <CenterFlex data-cy={'col-result-distributed-by'}>{getSupplierNameLabel(supplierName ?? '', t)}</CenterFlex>
    ),
  },
  {
    title: (
      <span data-cy={'col-header-external-order-number'}>
        {t('clients_order.supplier_order_number', 'External Order ref.')}
      </span>
    ),
    dataIndex: 'supplierOrderNumber',
    key: 'supplierOrderNumber',
    render: (supplierOrderNumber?: string) => (
      <CenterFlex data-cy={'col-result-external-order-number'}>{supplierOrderNumber ?? ''}</CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-dms-acknowledged'}>{t('clients_order.dms_acknowledged', 'DMS Ack.')}</span>,
    dataIndex: 'dmsAcknowledged',
    key: 'dmsAcknowledged',
    render: (dmsAcknowledged = false) => (
      <CenterFlex data-cy={'col-result-dms-acknowledged'}>
        {dmsAcknowledged ? (
          <Icon IconComponent={CheckCircleIcon} size={24} color={theme.color.palette_green_7} />
        ) : (
          <Icon IconComponent={CircleXMark} size={20} color={theme.color.palette_red_7} />
        )}
      </CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-urgent'}>{t('order.urgent', 'Urgent')}</span>,
    dataIndex: 'urgent',
    key: 'urgent',
    render: (urgent = false) => (
      <CenterFlex data-cy={'col-result-urgent'}>
        {urgent ? (
          <Icon IconComponent={CheckCircleIcon} size={24} color={theme.color.palette_green_7} />
        ) : (
          <Icon IconComponent={CircleXMark} size={20} color={theme.color.palette_red_7} />
        )}
      </CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-buyer'}>{t('clients_order.buyer', 'Buyer')}</span>,
    dataIndex: 'garageName',
    key: 'garageName',
    render: (garageName?: string) => <CenterFlex data-cy={'col-result-buyer'}>{garageName ?? ''}</CenterFlex>,
  },
  {
    title: (
      <span data-cy={'col-header-total-vat-excluded'}>
        {t('common.price.total_VAT_excluded', 'Total VAT Excluded')}
      </span>
    ),
    dataIndex: 'valueWithoutTax',
    key: 'valueWithoutTax',
    render: (_value: string, row: MyClientsOrder) => {
      const { valueWithoutTax, currency } = row;
      return (
        <CenterFlex data-cy={'col-result-total-vat-excluded'}>
          {textFormatter.formatCurrency(valueWithoutTax ?? 0, currency)}
        </CenterFlex>
      );
    },
  },
  {
    title: <span data-cy={'col-header-status'}>{t('order.order_detail.status', 'Status')}</span>,
    dataIndex: 'status',
    key: 'status',
    render: (status?: string) => (
      <CenterFlex data-cy={'col-result-status'}>{getMyClientsOrderStatus(status ?? '', t)}</CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-message'}>{t('order.order_detail.message', 'Message')}</span>,
    dataIndex: 'message',
    key: 'message',
    render: (message?: string) => (
      <CenterFlex data-cy={'col-result-message'}>
        {!!message && <IconButton IconComponent={EnvelopeOutlinedIcon} size={27} tooltip={message} />}
      </CenterFlex>
    ),
  },
];
